import React, { useState, useEffect, SyntheticEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Auth, Amplify } from 'aws-amplify';
import * as Sentry from '@sentry/browser';
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';

import { AuthVariant } from './AuthBox';
import ConfirmResetPassword from './ConfirmResetPassword';
import Container from '../../01_atoms/Container/Container';
import getUsernameByEmailAddress from '../../../utils/getUserEmailByAddress';
import gtagReportConversion from '../../../utils/gtagReportConversion';
import IconCheck from '../../../assets/icons/iconCheck.inline.svg';
import IconExclamation from '../../../assets/icons/iconExclamation.inline.svg';
import InformationModal, { IInformationScreen } from './InformationModal';
import ResetPassword from './ResetPassword';
import SignInForm from './SignInForm';
import SignUpForm from './SignUpForm';
import Text from '../../01_atoms/Text/Text';
import useAuth from '../../../hooks/useAuth';
import { navigate } from '@reach/router';
import ConfirmAuthCode from './ConfirmAuthCode';
import API from '../../../services/API';
import Spinner from '../../01_atoms/Spinner/Spinner';
import { useUser, useYougaClientApi } from '@youga/youga-client-api';
import BusinessSignInForm from './BusinessSignInForm';
import CodeInfoModal, {
  IInformationScreen as CodeIInformationScreen,
} from '../../03_organisms/MyAccountSection/CodeInformationModal';
import {
  getAuthErrorCode,
  getAuthErrorMessage,
  getVoucherErrorMessage,
  isVoucherRejected,
  waitForRequestConfirmation,
} from '../../../utils/authError';

const pendingVoucherStorageKey = 'stretchme.pendingVoucherCode';

function getTokenPayload(token: string): Record<string, unknown> | null {
  try {
    const encodedPayload = token.split('.')[1];
    const normalizedPayload = encodedPayload
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(encodedPayload.length / 4) * 4, '=');
    return JSON.parse(window.atob(normalizedPayload));
  } catch (_error) {
    return null;
  }
}

function getVoucherCodeFromToken(token: string): string {
  try {
    const payload = getTokenPayload(token);
    const code = String(payload?.['custom:code'] || '')
      .trim()
      .toUpperCase();
    return /^[A-Z0-9]{6}$/.test(code) ? code : '';
  } catch (_error) {
    return '';
  }
}

function isEmailVerifiedInToken(token: string): boolean {
  const value = getTokenPayload(token)?.email_verified;
  return value === true || value === 'true';
}

interface AuthFormProps {
  confirmCode?: string;
  initialEmail?: string;
  referral: string;
  variant?: AuthVariant;
  setCurrentVariant: (variant: AuthVariant) => void;
}

// Password validation functions
const hasLowerCase = (password: string): boolean => /[a-z]/.test(password);
const hasUpperCase = (password: string): boolean => /[A-Z]/.test(password);
const hasNumber = (password: string): boolean => /[0-9]/.test(password);
const hasSpecialChar = (password: string): boolean =>
  /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

export default function AuthForm({
  confirmCode,
  initialEmail,
  referral = '',
  variant = 'signin',
  setCurrentVariant,
}: AuthFormProps) {
  const { t, i18n } = useTranslation();
  const { handleSignInWithEmail, refreshAuthContext } = useAuth();
  const { fetcher, api, userId } = useYougaClientApi();

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState(initialEmail || '');
  const [purchaseCode, setPurchaseCode] = useState(() => {
    if (typeof window === 'undefined') {
      return '';
    }
    return window.sessionStorage.getItem(pendingVoucherStorageKey) || '';
  });
  const [isLoading, setIsLoading] = useState(false);
  const isSubmittingRef = useRef(false);
  const federatedSignInRef = useRef(false);
  const accountCreatedDuringSubmitRef = useRef(false);
  const [password, setPassword] = useState('');
  const { token, fetchAndSetCurrentUser } = useAuth();
  const { refreshUser } = useUser();
  const [newsletter, setNewsletter] = useState('false');
  const [conditionsAccepted, setConditionsAccepted] = useState(false);
  const [codeInformationScreen, setCodeInformationScreen] =
    useState<CodeIInformationScreen | null>(null);

  const [errorInformation, setErrorInformation] = useState<null | {
    position: string;
    element: React.ReactNode;
  }>(null);
  const [code, setCode] = useState(confirmCode || '');
  const [informationScreen, setInformationScreen] =
    useState<IInformationScreen | null>(null);
  const resetSearchParams =
    typeof window === 'undefined'
      ? null
      : new URLSearchParams(window.location.search);
  const passwordResetCode =
    code || confirmCode || resetSearchParams?.get('resetCode') || '';
  const passwordResetEmail =
    email || initialEmail || resetSearchParams?.get('email') || '';
  let title = '';

  switch (variant) {
    case 'signin':
      title = t('LOGE_DICH');
      break;
    case 'business_login':
      title = t('BUSINESS_LOGIN');
      break;
    case 'business_signup':
      title = t('BUSINESS_REGISTER');
      break;
    default:
      title = "Let's stretch together to achieve your goal";
      break;
  }

  let subtitle = '';

  switch (variant) {
    case 'resetPassword':
      subtitle = t('RESSET_YOUR_PASSWORD');
      break;
    case 'confirmResetPassword':
      subtitle = t('CONFIRM_RESET_PASSWORD');
      break;
    case 'business_signup':
      subtitle = '';
      break;
    case 'business_login':
      subtitle = '';
      break;
    default:
      subtitle = '';
      break;
  }

  useEffect(() => {
    setInformationScreen(null);

    setErrorInformation(null);
  }, [variant]);

  useEffect(() => {
    if (initialEmail) {
      setEmail(initialEmail);
    }
  }, [initialEmail]);

  useEffect(() => {
    if (confirmCode) {
      setCode(confirmCode);
    }
  }, [confirmCode]);

  const federatedSignIn = async (
    provider: CognitoHostedUIIdentityProvider,
  ): Promise<void> => {
    if (federatedSignInRef.current) return;
    federatedSignInRef.current = true;
    try {
      const response = await Auth.federatedSignIn({ provider });
    } catch (e) {
      console.log('Federated login failed: provider: ', { provider });
      console.log(e);
      setInformationScreen({
        allowClose: true,
        title: t('SORRY'),
        icon: IconExclamation,
        iconColor: 'warning',
        description: <span>{getAuthErrorMessage(t, e)}</span>,
      });
    } finally {
      federatedSignInRef.current = false;
    }
  };

  const onClickFacebookLogin = async (
    e: SyntheticEvent<HTMLButtonElement>,
  ): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();

    await federatedSignIn(CognitoHostedUIIdentityProvider.Facebook);
  };

  const clientCheck = async (client: string) => {
    const normalizedCode = client.trim().toUpperCase();
    setPurchaseCode(normalizedCode);
    if (typeof window !== 'undefined') {
      if (normalizedCode) {
        window.sessionStorage.setItem(pendingVoucherStorageKey, normalizedCode);
      } else {
        window.sessionStorage.removeItem(pendingVoucherStorageKey);
      }
    }
    return true;
  };

  const onClickGoogleLogin = async (
    e: SyntheticEvent<HTMLButtonElement>,
  ): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();

    await federatedSignIn(CognitoHostedUIIdentityProvider.Google);
  };

  const onClickAppleLogin = async (
    e: SyntheticEvent<HTMLButtonElement>,
  ): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();

    await federatedSignIn(CognitoHostedUIIdentityProvider.Apple);
  };
  console.log({ purchaseCode });

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];

    if (password.length < 12) {
      errors.push(t('MINIMAL_CHAR_LONG'));
    }
    if (!hasLowerCase(password)) {
      errors.push(t('LOWERCASE_REQUIRED'));
    }
    if (!hasUpperCase(password)) {
      errors.push(t('CAPITAL_LETTER_REQUIRED'));
    }
    if (!hasNumber(password)) {
      errors.push(t('NUMBER_REQUIRED'));
    }
    if (!hasSpecialChar(password)) {
      errors.push(t('SPECIAL_CHAR_REQUIRED'));
    }

    return errors;
  };

  const showEmailVerificationPrompt = async (): Promise<void> => {
    try {
      const deliveryStatus = await waitForRequestConfirmation(
        Auth.verifyCurrentUserAttribute('email'),
      );
      setInformationScreen({
        title:
          deliveryStatus === 'confirmed'
            ? t('EMAIL_SENT')
            : t('CHECK_EMAIL_INBOX'),
        description:
          deliveryStatus === 'confirmed'
            ? t('ENTER_CODE_FROM_EMAIL')
            : t('EMAIL_DELIVERY_UNCONFIRMED'),
        iconColor: 'primary',
        icon: IconCheck,
        buttonLabel: t('ENTER_THE_CODE'),
        buttonTargetVariant: 'confirmSignup',
      });
    } catch (error) {
      setInformationScreen({
        allowClose: true,
        title: t('SORRY'),
        description: <span>{getAuthErrorMessage(t, error)}</span>,
        iconColor: 'warning',
        icon: IconExclamation,
        buttonLabel: t('ENTER_THE_CODE'),
        buttonTargetVariant: 'confirmSignup',
      });
    }
  };

  const onSubmit = async (
    e: SyntheticEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;
    accountCreatedDuringSubmitRef.current = false;
    setInformationScreen(null);
    setErrorInformation(null);

    setLoading(true);

    try {
      switch (variant) {
        case 'signin':
          /**
           * @see https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_InitiateAuth.html
           * @see https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_RespondToAuthChallenge.html
           * @see https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_GetUser.html
           */
          try {
            await handleSignInWithEmail(email, password);
            if (purchaseCode.length > 0) {
              try {
                const newToken = await fetchAndSetCurrentUser();
                if (newToken as any) {
                  if (!isEmailVerifiedInToken(newToken)) {
                    await showEmailVerificationPrompt();
                    break;
                  }
                  await API.applyVoucher(newToken, purchaseCode.trim());
                  window.sessionStorage.removeItem(pendingVoucherStorageKey);
                  setCodeInformationScreen({
                    allowClose: false,
                    title: t('voucher_activated'),
                    description: t('voucher_activated_desc'),
                    buttonLabel: t('voucher_activated_label'),
                    iconColor: 'success',
                    onLabelClick: async () => {
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      window.location = '/app';
                    },
                  });
                }
              } catch (error) {
                if (isVoucherRejected(error)) {
                  window.sessionStorage.removeItem(pendingVoucherStorageKey);
                }
                setCodeInformationScreen({
                  allowClose: true,
                  title: isVoucherRejected(error)
                    ? t('voucher_code_invalid')
                    : t('SORRY'),
                  description: getVoucherErrorMessage(t, error),
                  buttonLabel: t('voucher_code_invalid_label'),
                  iconColor: isVoucherRejected(error) ? 'error' : 'warning',
                });
                break;
              }
            } else {
              await fetchAndSetCurrentUser();
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              window.location = '/app';
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } catch (error: any) {
            let caughtError = false;

            // We only want to catch API specific errors here
            // in all other cases, we throw the error one level higher
            switch (error.code) {
              // This exception is thrown when a user is not confirmed successfully.
              case 'UserNotConfirmedException':
                caughtError = true;
                setErrorInformation({
                  position: 'email',
                  element: <span>{t('CONFIRM_ACCOUT_FIRST')}</span>,
                });
                break;

              // This exception is thrown when a user is not found.
              case 'UserNotFoundException':
                caughtError = true;
                setInformationScreen({
                  title: t('USER_NOT_FOUND'),
                  icon: IconExclamation, // @todo find a better icon
                  iconColor: 'warning',
                  description: '',
                  buttonLabel: t('REGISTER_BUTTON_LINK'),
                  buttonTargetVariant: 'business_signup',
                });
                break;

              // This exception is thrown when the Amazon Cognito service encounters an invalid password.
              case 'InvalidPasswordException':
                caughtError = true;
                setErrorInformation({
                  position: 'password',
                  element: <span>{t('INVALID_PASSWORD')}</span>,
                });
                break;

              // This exception is thrown when a user is not authorized.
              // Actually, this error is also thrown when a user does not exist or is not activated
              case 'NotAuthorizedException':
                caughtError = true;
                setErrorInformation({
                  position: 'email',
                  element: <span>{t('INVALID_LOGIN_OR_PASSWORD')}</span>,
                });
                break;

              default:
                caughtError = false;
                break;
            }

            if (!caughtError) {
              throw error;
            }
          }

          break;

        case 'business_signup':
          /**
           * @see https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_SignUp.html
           */
          try {
            setIsLoading(true);

            // Validate password before attempting signup
            const passwordErrors = validatePassword(password);
            if (passwordErrors.length > 0) {
              setErrorInformation({
                position: 'password',
                element: (
                  <span>
                    {t('PASSWORD_NOT_MEET_REQUIREMENTS')}
                    <br />
                    {passwordErrors.map((error, index) => (
                      <span key={index}>
                        - {error}
                        <br />
                      </span>
                    ))}
                  </span>
                ),
              });
              return;
            }

            if (purchaseCode && !/^[A-Z0-9]{6}$/.test(purchaseCode)) {
              setErrorInformation({
                position: 'voucher',
                element: <span>{t('voucher_code_invalid')}</span>,
              });
              return;
            }

            await Auth.signUp({
              username: getUsernameByEmailAddress(email),
              password,
              clientMetadata: {
                autoConfirm: 'true',
                locale: i18n.language !== 'pl' ? 'eng' : 'pl',
              },
              attributes: {
                email: email.trim().toLowerCase(),
                'custom:referral': 'app',
                'custom:locale': i18n.language !== 'pl' ? 'eng' : 'pl',
                'custom:terms_agreed': 'true',
                'custom:newsletter': newsletter,
                'custom:code': purchaseCode,
              },
            });
            accountCreatedDuringSubmitRef.current = true;

            const resultSignIn = await Auth.signIn(
              // if the email address is not yet verified, we need to give the username here instead the email address
              getUsernameByEmailAddress(email),
              password,
            );

            console.log(`SignUp::signin::result:`, resultSignIn);
            const deliveryStatus = await waitForRequestConfirmation(
              Auth.verifyCurrentUserAttribute('email'),
            );

            setInformationScreen({
              title:
                deliveryStatus === 'confirmed'
                  ? t('EMAIL_SENT')
                  : t('CHECK_EMAIL_INBOX'),
              description:
                deliveryStatus === 'confirmed'
                  ? t('ENTER_CODE_FROM_EMAIL')
                  : t('EMAIL_DELIVERY_UNCONFIRMED'),
              iconColor: 'primary',
              icon: IconCheck,
              buttonLabel: t('ENTER_THE_CODE'),
              buttonTargetVariant: 'confirmSignup',
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } catch (error: any) {
            if (accountCreatedDuringSubmitRef.current) {
              setInformationScreen({
                allowClose: true,
                title: t('SORRY'),
                description: t('ACCOUNT_CREATED_CODE_SEND_FAILED'),
                icon: IconExclamation,
                iconColor: 'warning',
                buttonLabel: t('LOGIN_BUTTON_LINK'),
                buttonTargetVariant: 'signin',
              });
              break;
            }
            let caughtError = false;

            // We only want to catch API specific errors here
            // in all other cases, we throw the error one level higher
            switch (error.code) {
              // This exception is thrown when the Amazon Cognito service encounters an invalid parameter.
              case 'InvalidParameterException':
              case 'InvalidPasswordException':
                if (
                  error.message.includes(
                    "Value at 'password' failed to satisfy constraint",
                  ) ||
                  error.message.includes(
                    'Password did not conform with policy',
                  ) ||
                  error.message.includes('Password does not conform to policy')
                ) {
                  caughtError = true;
                  const passwordErrors = validatePassword(password);
                  setErrorInformation({
                    position: 'password',
                    element: (
                      <span>
                        {t('PASSWORD_NOT_MEET_REQUIREMENTS')}
                        <br />
                        {passwordErrors.map((error, index) => (
                          <span key={index}>
                            - {error}
                            <br />
                          </span>
                        ))}
                      </span>
                    ),
                  });
                  break;
                }

                if (error.message.includes('Invalid email address format.')) {
                  caughtError = true;
                  setErrorInformation({
                    position: 'email',
                    element: <span>{t('EMAIL_INVALID')}</span>,
                  });
                  break;
                }

                break;

              // This exception is thrown when Amazon Cognito encounters a user name that already exists in the user pool.
              case 'UsernameExistsException':
                caughtError = true;
                setInformationScreen({
                  title: t('USER_ALREADY_EXISITS'),
                  icon: IconExclamation, // @todo find a better icon
                  iconColor: 'warning',
                  description: '',
                  buttonLabel: t('LOGIN_BUTTON_LINK'),
                  buttonTargetVariant: 'signin',
                });
                break;

              default:
                caughtError = false;
                break;
            }

            if (!caughtError) {
              throw error;
            }
          } finally {
            setIsLoading(false);
          }
          break;

        case 'confirmSignup':
          /**
           * @see https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_ConfirmSignUp.html
           */
          try {
            setIsLoading(true);
            await refreshAuthContext(code.trim());
            // The cached Cognito session still contains email_verified=false.
            // Force a fresh ID token before syncing the profile or applying a
            // pending voucher.
            const newToken = await fetchAndSetCurrentUser(true);
            if (newToken) {
              await API.postUserAttributes(newToken, { email_verified: true });
              const voucherCode =
                purchaseCode || getVoucherCodeFromToken(newToken);
              if (voucherCode) {
                try {
                  await API.applyVoucher(newToken, voucherCode);
                  window.sessionStorage.removeItem(pendingVoucherStorageKey);
                } catch (voucherError) {
                  if (isVoucherRejected(voucherError)) {
                    window.sessionStorage.removeItem(pendingVoucherStorageKey);
                  }
                  setCodeInformationScreen({
                    allowClose: !isVoucherRejected(voucherError),
                    title: isVoucherRejected(voucherError)
                      ? t('voucher_code_invalid')
                      : t('SORRY'),
                    description: getVoucherErrorMessage(t, voucherError),
                    buttonLabel: t('voucher_code_invalid_label'),
                    iconColor: isVoucherRejected(voucherError)
                      ? 'error'
                      : 'warning',
                    onLabelClick: () => {
                      window.location.assign('/app');
                    },
                  });
                  return;
                }
              }
            }
            try {
              await refreshUser();
            } catch (refreshError) {
              console.log(
                'Email confirmed; profile refresh failed',
                refreshError,
              );
            }
            try {
              await gtagReportConversion();
            } catch (analyticsError) {
              console.log('Signup conversion analytics failed', analyticsError);
            }
            await navigate('/app');
            return;
          } catch (error: any) {
            let caughtError = false;

            // We only want to catch API specific errors here
            // in all other cases, we throw the error one level higher
            switch (error.code) {
              // This exception is thrown if the provided code does not match what the server was expecting.
              case 'CodeMismatchException':
                caughtError = true;
                setErrorInformation({
                  position: 'code',
                  element: <span>{t('ACTIVATION_CODE_INVALID')}</span>,
                });
                break;

              // This exception is thrown if a code has expired.
              case 'ExpiredCodeException':
                // @todo add instructions on how to retrieve new code
                caughtError = true;
                setInformationScreen({
                  allowClose: true,
                  title: t('CODE_EXPIRED'),
                  icon: IconExclamation, // @todo find a better icon
                  iconColor: 'error',
                  description: (
                    <span>
                      {t('CODE_EXPIRED')} {t('SEND_CODE_AGAIN')}
                    </span>
                  ),
                });
                break;

              case 'UserNotFoundException':
                caughtError = true;
                setErrorInformation({
                  position: 'email',
                  element: <span>{t('EMAIL_NOT_FOUND')}</span>,
                });
                break;

              default:
                caughtError = false;
                break;
            }

            if (!caughtError) {
              throw error;
            }
            break;
          } finally {
            setIsLoading(false);
          }

          break;

        case 'resetPassword':
          try {
            /**
             * @see https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_ForgotPassword.html
             */
            const deliveryStatus = await waitForRequestConfirmation(
              Auth.forgotPassword(
                email.trim().toLowerCase().replace('@', '_at_'),
                { resetTarget: 'app' },
              ),
            );

            // Unfortunately we do not get any error information on whether the user exists or not
            // so we need to display a generic help message in case the user does not get any email
            setInformationScreen({
              title:
                deliveryStatus === 'confirmed'
                  ? t('EMAIL_SENT')
                  : t('CHECK_EMAIL_INBOX'),
              description: (
                <>
                  <p>
                    {deliveryStatus === 'confirmed'
                      ? t('ENTER_CODE_TO_CHANGE_PASSWORD')
                      : t('EMAIL_DELIVERY_UNCONFIRMED')}
                  </p>
                  <p>
                    {t('CONTACT_US_IF_YOU_HAVENT_RECIEVE_EMAIL')}{' '}
                    <a href={`mailto:${t('INFO_MAIL')}`}>{t('INFO_MAIL')}</a>
                  </p>
                </>
              ),
              iconColor: 'primary',
              icon: IconCheck,
              buttonLabel: t('ENTER_THE_CODE'),
              buttonTargetVariant: 'confirmResetPassword',
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } catch (error: any) {
            let caughtError = false;

            // We only want to catch API specific errors here
            // in all other cases, we throw the error one level higher
            switch (error.code) {
              // This exception is thrown when a user is not confirmed successfully.
              case 'UserNotConfirmedException':
                caughtError = true;
                setErrorInformation({
                  position: 'email',
                  element: <span>{t('CONFIRM_ACCOUT_FIRST')}</span>,
                });
                break;

              // This exception is thrown when a user is not found.
              case 'UserNotFoundException':
                caughtError = true;
                setErrorInformation({
                  position: 'email',
                  element: <span>{t('EMAIL_NOT_FOUND')}</span>,
                });
                break;

              default:
                caughtError = false;
                break;
            }

            if (!caughtError) {
              throw error;
            }
          }
          break;

        case 'confirmResetPassword': {
          let passwordResetSucceeded = false;
          try {
            /**
             * @see https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_ConfirmForgotPassword.html
             */

            // Validate password before attempting reset
            const passwordErrors = validatePassword(password);
            if (passwordErrors.length > 0) {
              setErrorInformation({
                position: 'password',
                element: (
                  <span>
                    {t('PASSWORD_NOT_MEET_REQUIREMENTS')}
                    <br />
                    {passwordErrors.map((error, index) => (
                      <span key={index}>
                        - {error}
                        <br />
                      </span>
                    ))}
                  </span>
                ),
              });
              return;
            }

            await Auth.forgotPasswordSubmit(
              passwordResetEmail.trim().toLowerCase().replace('@', '_at_'),
              passwordResetCode.trim(),
              password,
            );
            passwordResetSucceeded = true;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } catch (error: any) {
            let caughtError = false;

            // We only want to catch API specific errors here
            // in all other cases, we throw the error one level higher
            switch (error.code) {
              // This exception is thrown when the Amazon Cognito service encounters an invalid parameter.
              case 'InvalidParameterException':
              case 'InvalidPasswordException':
                if (
                  error.message.includes(
                    "Value at 'password' failed to satisfy constraint",
                  ) ||
                  error.message.includes(
                    'Password did not conform with policy',
                  ) ||
                  error.message.includes('Password does not conform to policy')
                ) {
                  caughtError = true;
                  const passwordErrors = validatePassword(password);
                  setErrorInformation({
                    position: 'password',
                    element: (
                      <span>
                        {t('PASSWORD_NOT_MEET_REQUIREMENTS')}
                        <br />
                        {passwordErrors.map((error, index) => (
                          <span key={index}>
                            - {error}
                            <br />
                          </span>
                        ))}
                      </span>
                    ),
                  });
                  break;
                }
                break;

              // This exception is thrown if the provided code does not match what the server was expecting.
              case 'CodeMismatchException':
                caughtError = true;
                setErrorInformation({
                  position: 'code',
                  element: <span>{t('ACTIVATION_CODE_INVALID')}</span>,
                });
                break;

              // This exception is thrown if a code has expired.
              case 'ExpiredCodeException':
                // @todo add instructions on how to retrieve new code
                caughtError = true;
                setInformationScreen({
                  allowClose: true,
                  title: 'Dein Code ist abgelaufen.',
                  icon: IconExclamation, // @todo find a better icon
                  iconColor: 'error',
                  description: (
                    <span>
                      {t('CODE_EXPIRED')} {t('SEND_CODE_AGAIN')}
                    </span>
                  ),
                });
                break;

              // This exception is thrown when a user is not confirmed successfully.
              case 'UserNotConfirmedException':
                caughtError = true;
                setErrorInformation({
                  position: 'email',
                  element: <span>{t('CONFIRM_ACCOUNT_FIRST')}</span>,
                });
                break;

              // This exception is thrown when a user is not found.
              case 'UserNotFoundException':
                caughtError = true;
                setErrorInformation({
                  position: 'email',
                  element: <span>{t('EMAIL_NOT_FOUND')}</span>,
                });
                break;

              default:
                caughtError = false;
                break;
            }

            if (!caughtError) {
              throw error;
            }
            break;
          }

          if (!passwordResetSucceeded) {
            break;
          }

          // We know that the user came here from the previous steps -> direct login because we know the password
          if (password) {
            try {
              /**
               * @see https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_InitiateAuth.html
               * @see https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_RespondToAuthChallenge.html
               * @see https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_GetUser.html
               */
              await handleSignInWithEmail(email, password);
              window.location.assign('/app');
            } catch (error) {
              // This is a special case here, because the "signin" is somehow optional
              // if it fails, we display the user an information screen and redirect him to the login
              setInformationScreen({
                title: t('PASSWORD_CHANGED'),
                description: t('PASSWORD_CHANGED_SUCCESSFULLY'),
                iconColor: 'primary',
                icon: IconCheck,
                buttonLabel: t('LOGIN_BUTTON_LINK'),
                buttonTargetVariant: 'signin',
              });
            }
          } else {
            setInformationScreen({
              title: t('PASSWORD_CHANGED'),
              description: t('PASSWORD_CHANGED_SUCCESSFULLY'),
              iconColor: 'primary',
              icon: IconCheck,
              buttonLabel: t('LOGIN_BUTTON_LINK'),
              buttonTargetVariant: 'signin',
            });
          }
          break;
        }

        case 'business_login':
          /**
           * @see https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_InitiateAuth.html
           * @see https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_RespondToAuthChallenge.html
           * @see https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_GetUser.html
           */
          try {
            await handleSignInWithEmail(email, password);
            if (purchaseCode.length > 0) {
              try {
                const newToken = await fetchAndSetCurrentUser();
                if (newToken as any) {
                  if (!isEmailVerifiedInToken(newToken)) {
                    await showEmailVerificationPrompt();
                    break;
                  }
                  await API.applyVoucher(newToken, purchaseCode.trim());
                  window.sessionStorage.removeItem(pendingVoucherStorageKey);
                  setCodeInformationScreen({
                    allowClose: false,
                    title: t('voucher_activated'),
                    description: t('voucher_activated_desc'),
                    buttonLabel: t('voucher_activated_label'),
                    iconColor: 'success',
                    onLabelClick: async () => {
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      window.location = '/app';
                    },
                  });
                }
              } catch (error) {
                if (isVoucherRejected(error)) {
                  window.sessionStorage.removeItem(pendingVoucherStorageKey);
                }
                setCodeInformationScreen({
                  allowClose: true,
                  title: isVoucherRejected(error)
                    ? t('voucher_code_invalid')
                    : t('SORRY'),
                  description: getVoucherErrorMessage(t, error),
                  buttonLabel: t('voucher_code_invalid_label'),
                  iconColor: isVoucherRejected(error) ? 'error' : 'warning',
                });
                break;
              }
            } else {
              await fetchAndSetCurrentUser();
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              window.location = '/app';
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } catch (error: any) {
            let caughtError = false;

            // We only want to catch API specific errors here
            // in all other cases, we throw the error one level higher
            switch (error.code) {
              // This exception is thrown when a user is not confirmed successfully.
              case 'UserNotConfirmedException':
                caughtError = true;
                setErrorInformation({
                  position: 'email',
                  element: <span>{t('CONFIRM_ACCOUT_FIRST')}</span>,
                });
                break;

              // This exception is thrown when a user is not found.
              case 'UserNotFoundException':
                caughtError = true;
                setInformationScreen({
                  title: t('USER_NOT_FOUND'),
                  icon: IconExclamation, // @todo find a better icon
                  iconColor: 'warning',
                  description: '',
                  buttonLabel: t('REGISTER_BUTTON_LINK'),
                  buttonTargetVariant: 'business_signup',
                });
                break;

              // This exception is thrown when the Amazon Cognito service encounters an invalid password.
              case 'InvalidPasswordException':
                caughtError = true;
                setErrorInformation({
                  position: 'password',
                  element: <span>{t('INVALID_PASSWORD')}</span>,
                });
                break;

              // This exception is thrown when a user is not authorized.
              // Actually, this error is also thrown when a user does not exist or is not activated
              case 'NotAuthorizedException':
                caughtError = true;
                setErrorInformation({
                  position: 'email',
                  element: <span>{t('INVALID_LOGIN_OR_PASSWORD')}</span>,
                });
                break;

              default:
                caughtError = false;
                break;
            }

            if (!caughtError) {
              throw error;
            }
          }

          break;

        default:
          break;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      Sentry.configureScope((scope) => {
        const errorCode = getAuthErrorCode(err);
        const encodeForDiagnostics = (value: unknown) => {
          try {
            return btoa(unescape(encodeURIComponent(String(value ?? ''))));
          } catch {
            return '';
          }
        };
        scope.setExtra('errorCode', errorCode);
        scope.setExtra('errorCodeB64', encodeForDiagnostics(errorCode));
        scope.setExtra('errorMessage', err?.message);
        scope.setExtra('errorMessageB64', encodeForDiagnostics(err?.message));
        scope.setExtra('errorName', err?.name);
        scope.setExtra('errorNameB64', encodeForDiagnostics(err?.name));
        Sentry.captureException(
          new Error('Signup Flow Uncaught Cognito Error Response'),
        );
      });

      setInformationScreen({
        allowClose: true,
        title: t('SORRY'),
        icon: IconExclamation, // @todo find a better icon
        iconColor: 'warning',
        description: <span>{getAuthErrorMessage(t, err)}</span>,
      });
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  return (
    <>
      <InformationModal
        informationScreen={informationScreen}
        setInformationScreen={setInformationScreen}
        setCurrentVariant={setCurrentVariant}
      />

      <form
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        sx={{ m: 0, p: 0 }}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onSubmit={onSubmit}
      >
        <Container sx={{ width: '24rem' }}>
          <Text variant="h1" style={{ marginBottom: '2rem' }}>
            {title}
          </Text>

          <Text
            style={{
              color: 'rgb(17, 17, 17)',
              fontSize: '24px',
              fontWeight: 'bold',
              marginBottom: '2rem',
            }}
          >
            {subtitle}
          </Text>

          {variant === 'signin' && (
            <SignInForm
              errorInformation={errorInformation}
              // onClickFacebookLogin={onClickFacebookLogin}
              onClickGoogleLogin={onClickGoogleLogin}
              onClickAppleLogin={onClickAppleLogin}
              setEmail={setEmail}
              setPassword={setPassword}
              clientCheck={clientCheck}
              setCurrentVariant={setCurrentVariant}
              loading={loading}
            />
          )}

          {variant === 'resetPassword' && (
            <ResetPassword
              errorInformation={errorInformation}
              setCurrentVariant={setCurrentVariant}
              setEmail={setEmail}
            />
          )}

          {variant === 'confirmResetPassword' && (
            <ConfirmResetPassword
              code={passwordResetCode}
              email={passwordResetEmail}
              errorInformation={errorInformation}
              setCurrentVariant={setCurrentVariant}
              setCode={setCode}
              setPassword={setPassword}
            />
          )}

          {variant === 'confirmSignup' && (
            <ConfirmAuthCode
              confirmCode={confirmCode}
              errorInformation={errorInformation}
              setCurrentVariant={setCurrentVariant}
              setCode={setCode}
              setPassword={setPassword}
              setIsLoading={setIsLoading}
            />
          )}

          {variant === 'business_signup' && (
            <SignUpForm
              errorInformation={errorInformation}
              onClickFacebookLogin={onClickFacebookLogin}
              onClickGoogleLogin={onClickGoogleLogin}
              onClickAppleLogin={onClickAppleLogin}
              setEmail={setEmail}
              clientCheck={clientCheck}
              setPassword={setPassword}
              setCurrentVariant={setCurrentVariant}
              conditionsAccepted={conditionsAccepted}
              setConditionsAccepted={setConditionsAccepted}
              newsletter={newsletter}
              setNewsletter={setNewsletter}
              setCode={setCode}
              confirmCode={confirmCode}
            />
          )}
          {variant === 'business_login' && (
            <BusinessSignInForm
              errorInformation={errorInformation}
              setEmail={setEmail}
              clientCheck={clientCheck}
              setPassword={setPassword}
              isLoading={isLoading}
            />
          )}
        </Container>
        <CodeInfoModal
          informationScreen={codeInformationScreen}
          setInformationScreen={setCodeInformationScreen}
        />
      </form>
      {isLoading && <Spinner fullscreen />}
    </>
  );
}
